if (!customElements.get("custom-pagination")) {
  class CustomPagination extends HTMLElement {
    constructor() {
      super();
      this.reloadHandler = this.reloadHandler || this.reload.bind(this);
      this.init();
      document.addEventListener("on:bfcache:load-restore", this.reloadHandler);
    }

    disconnectedCallback() {
      this.destroy();
      document.removeEventListener("on:bfcache:load-restore", this.reloadHandler);
    }

    init() {
      this.infiniteScroll = this.dataset.infiniteScroll === "true";
      this.pagination = this.querySelector(".js-pagination");
      this.loadMoreButton = this.querySelector(".js-pagination-load-more");
      this.results = this.querySelectorAll(".js-pagination-result");
      this.controller = null;
      this.isFetching = false;

      this.resultClickHandler =
        this.resultClickHandler || this.handleResultClick.bind(this);
      this.loadMoreClickHandler =
        this.loadMoreClickHandler || this.handleLoadMoreClick.bind(this);

      this.removeEventListener("click", this.resultClickHandler);
      this.addEventListener("click", this.resultClickHandler);

      if (
        this.pagination?.dataset.isMoreResults === "true" &&
        this.results.length > 0
      ) {
        if (this.loadMoreButton) {
          this.loadMoreButton.removeEventListener("click", this.loadMoreClickHandler);
          this.loadMoreButton.addEventListener("click", this.loadMoreClickHandler);
        }

        if (this.infiniteScroll) {
          this.paginationObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  this.autoLoadNextPage();
                  if (!this.intersectingTimer) {
                    this.intersectingTimer = window.setInterval(
                      () => this.autoLoadNextPage(),
                      1000
                    );
                  }
                } else {
                  this.clearAutoLoadInterval();
                }
              });
            },
            { rootMargin: `${window.innerHeight * 1.5}px 0px` }
          );
          this.paginationObserver.observe(this.pagination);
        }
      }
    }

    clearAutoLoadInterval() {
      if (this.intersectingTimer) {
        window.clearInterval(this.intersectingTimer);
        this.intersectingTimer = null;
      }
    }

    clearAutoLoad() {
      this.clearAutoLoadInterval();
      this.paginationObserver?.disconnect();
      this.paginationObserver = null;
    }

    async autoLoadNextPage() {
      const loaded = await this.loadMore();
      if (!loaded && !this.isFetching) this.clearAutoLoadInterval();
    }

    cancelLoadMore() {
      this.controller?.abort("Pagination interrupted");
      this.loadMoreButton?.classList.remove("is-loading");
      this.loadMoreButton?.removeAttribute("aria-disabled");
      this.pagination?.classList.remove("is-loading");
      this.controller = null;
      this.isFetching = false;
    }

    destroy() {
      this.clearAutoLoad();
      this.cancelLoadMore();
      this.loadMoreButton?.removeEventListener("click", this.loadMoreClickHandler);
      this.removeEventListener("click", this.resultClickHandler);
    }

    reload() {
      this.destroy();
      this.init();
    }

    handleLoadMoreClick(event) {
      event.preventDefault();
      this.loadMore();
    }

    handleResultClick(event) {
      if (event.target.closest("a") && event.target.closest(".js-pagination-result")) {
        this.cancelLoadMore();
      }
    }

    async loadMore() {
      if (this.isFetching || this.dataset.pauseInfiniteScroll === "true") {
        return false;
      }

      if (!this.loadMoreButton?.href) return false;

      this.isFetching = true;
      const controller = new AbortController();
      this.controller = controller;

      try {
        this.loadMoreButton.classList.add("is-loading");
        this.loadMoreButton.setAttribute("aria-disabled", "true");
        if (this.pagination?.dataset.paginationStyle === "traditional") {
          this.pagination.classList.add("is-loading");
        }

        const response = await fetch(this.loadMoreButton.href, {
          signal: controller.signal
        });
        if (!response.ok) throw new Error(`Pagination request failed (${response.status})`);
        if (this.dataset.pauseInfiniteScroll === "true") return false;

        const template = document.createElement("template");
        template.innerHTML = await response.text();
        const incomingResults = [
          ...template.content.querySelectorAll(".js-pagination-result")
        ];
        const lastResult = this.results[this.results.length - 1];
        if (!lastResult || incomingResults.length === 0) return false;

        lastResult.insertAdjacentHTML(
          "afterend",
          incomingResults.map((result) => result.outerHTML).join("")
        );
        if (typeof window.initLazyImages === "function") window.initLazyImages();
        this.results = this.querySelectorAll(".js-pagination-result");

        const incomingPagination = template.content.querySelector(".js-pagination");
        if (incomingPagination?.dataset.isMoreResults === "true") {
          this.pagination.innerHTML = incomingPagination.innerHTML;
          this.loadMoreButton?.removeEventListener(
            "click",
            this.loadMoreClickHandler
          );
          this.loadMoreButton = this.querySelector(".js-pagination-load-more");
          this.loadMoreButton?.addEventListener("click", this.loadMoreClickHandler);
        } else {
          this.pagination?.remove();
          this.pagination = null;
          this.loadMoreButton = null;
          this.clearAutoLoad();
          document.querySelectorAll(".js-when-paginated-only").forEach((element) => {
            element.remove();
          });
        }

        return true;
      } catch (error) {
        if (error?.name !== "AbortError" && error !== "Pagination interrupted") {
          console.warn("Unable to fetch next page of results", error);
        }
        return false;
      } finally {
        if (this.controller === controller) {
          this.controller = null;
          this.isFetching = false;
          this.loadMoreButton?.classList.remove("is-loading");
          this.loadMoreButton?.removeAttribute("aria-disabled");
          if (this.pagination?.dataset.paginationStyle === "traditional") {
            this.pagination.classList.remove("is-loading");
          }
        }
      }
    }
  }

  customElements.define("custom-pagination", CustomPagination);
}
